import React, { useRef, useEffect, useState, useCallback, useMemo } from 'react';
import { Sphere, ConnectingPerspective } from 'types/global';
import { useSchemaType } from '../../../../hooks/useSchemaType';
import { PermissionList } from 'types/permissions';
import { MultiUrgencyPillMolecule, PerspectiveUrgency } from '../../MultiUrgencyPill';

interface AtlasMultiUrgencyPillProps {
  isOpen: boolean;
  onClose: () => void;
  onUrgencyChange: (urgency: string, edgeId: string) => void;
  connectingPerspectives: ConnectingPerspective[];
  parentNode?: Sphere;
  fetchPermissionList: (
    sphereId: string,
    nodeLabel: string,
    permissions?: string[]
  ) => Promise<PermissionList | null>;
  sphereType: string;
}

const AtlasMultiUrgencyPillMolecule: React.FC<AtlasMultiUrgencyPillProps> = ({
  isOpen,
  onClose,
  onUrgencyChange,
  connectingPerspectives,
  parentNode,
  fetchPermissionList,
  sphereType,
}) => {
  const pillRef = useRef<HTMLDivElement>(null);
  const [hasPermission, setHasPermission] = useState<boolean | null>(null);

  const { schema: sphereSchema } = useSchemaType(sphereType ?? null);

  // Resolve perspective system names to display names
  const getPerspectiveDisplayName = useCallback(
    (systemName: string): string => {
      if (!systemName) return '';

      const perspectives = sphereSchema?.perspectives || [];
      const match = perspectives.find(
        (p: { system_name?: string }) => p.system_name === systemName
      );
      if (match?.name) {
        return match.name;
      }

      return systemName
        .replace('center_', '')
        .replaceAll('_', ' ')
        .split(' ')
        .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
    },
    [sphereSchema],
  );

  // Transform connectingPerspectives to PerspectiveUrgency format
  const perspectives: PerspectiveUrgency[] = useMemo(() => {
    return connectingPerspectives
      .filter((p) => p.edgeId) // Only include perspectives with edgeId
      .map((p) => ({
        edgeId: p.edgeId!,
        perspectiveName: getPerspectiveDisplayName(p.system_name || p.name || ''),
        urgency: Number(p.urgency) || 2,
      }));
  }, [connectingPerspectives, getPerspectiveDisplayName]);

  // Calculate aggregated urgency (max across all perspectives)
  const aggregatedUrgency = useMemo(() => {
    if (perspectives.length === 0) return 2;
    return Math.max(...perspectives.map((p) => p.urgency));
  }, [perspectives]);

  // Check permissions before rendering
  const checkPermission = useCallback(async () => {
    if (!parentNode?.id) {
      setHasPermission(true);
      return;
    }
    try {
      setHasPermission(null);
      const permissionList = await fetchPermissionList(
        parentNode.id,
        parentNode.label ?? '',
        ['edit']
      );
      const canEdit = permissionList?.permissions?.edit ?? false;
      if (canEdit) {
        setHasPermission(true);
      } else {
        onClose();
      }
    } catch (error) {
      console.error('AtlasMultiUrgencyPill: Permission check failed:', error);
      onClose();
    }
  }, [parentNode, fetchPermissionList, onClose]);

  useEffect(() => {
    if (isOpen) {
      checkPermission();
    }
  }, [isOpen, checkPermission]);

  // Handle outside clicks
  useEffect(() => {
    const handleDocClick = (ev: MouseEvent) => {
      if (
        isOpen &&
        pillRef.current &&
        !pillRef.current.contains(ev.target as Node)
      ) {
        onClose();
      }
    };
    document.addEventListener('mousedown', handleDocClick);
    return () => {
      document.removeEventListener('mousedown', handleDocClick);
    };
  }, [isOpen, onClose]);

  if (hasPermission !== true || !isOpen) {
    return null;
  }

  return (
    <div className="atlas-multi-urgency-pill" ref={pillRef}>
      <MultiUrgencyPillMolecule
        perspectives={perspectives}
        aggregatedUrgency={aggregatedUrgency}
        onUrgencyChange={onUrgencyChange}
      />
    </div>
  );
};

export default AtlasMultiUrgencyPillMolecule;
